/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { System } from '../accessors/System'; // tslint:disable:no-any
// tslint:disable: no-unsafe-any

/**
 * builder to create an Accessor for a special datastructure
 */

export class CrownAccessorBuilder {
  /**
   * constructor
   * @param isLE if true the data uses the little endian format
   * @param elementTypes the description of the datastructure e.g. ['float32', 'uint32', 'int8']
   * @param elementNames the names for the members inside an element e.g. ['pos', 'col', 'flag']. Default are : [e0, e1, e2, ...]
   */
  constructor(isLE, elementTypes, elementNames) {
    this._neededTypedArrays = new Set();
    this._neededCacheArrays = new Set();
    this._elements = [];
    this._sizeInfo = new Map([['int8', 1], ['uint8', 1], ['char8', 1], ['int16', 2], ['uint16', 2], ['int32', 4], ['uint32', 4], ['float32', 4], ['int64', 8], ['uint64', 8], ['float64', 8]]);
    this._arrayTypes = new Map([['int8', Int8Array], ['uint8', Uint8Array], ['char8', Uint8Array], ['int16', Int16Array], ['uint16', Uint16Array], ['int32', Int32Array], ['uint32', Uint32Array], ['float32', Float32Array], ['int64', null], ['uint64', null], ['float64', Float64Array]]);
    this._arrayTypesAsString = new Map([['int8', 'Int8Array'], ['uint8', 'Uint8Array'], ['char8', 'Uint8Array'], ['int16', 'Int16Array'], ['uint16', 'Uint16Array'], ['int32', 'Int32Array'], ['uint32', 'Uint32Array'], ['float32', 'Float32Array'], ['int64', null], ['uint64', null], ['float64', 'Float64Array']]);
    this._creatorFunctions = new Map([['int8', this._createInt8Access], ['uint8', this._createUint8Access], ['char8', this._createUint8Access], ['int16', this._createInt16Access], ['uint16', this._createUint16Access], ['int32', this._createInt32Access], ['uint32', this._createUint32Access], ['float32', this._createFloat32Access], ['int64', null], ['uint64', null], ['float64', this._createFloat64Access]]);
    this._formatIsLE = isLE;

    for (const i in elementTypes) {
      const name = !!elementNames ? elementNames[i] : 'e' + i;

      this._elements.push({
        type: elementTypes[i],
        index: i,
        name
      });
    }
  }
  /**
   * creates the Accesor
   */


  build() {
    this._swap = this._formatIsLE !== System.isLE();

    const blockSize = this._calculateBlockSize();

    this._calculateAlignmentInfo(blockSize);

    const accessor = this._createAccessor(blockSize);

    for (const elem of this._elements) {
      if (!!elem.name) {
        this._createFunction(accessor, 'get' + elem.name, this._creatorFunctions.get(elem.type).bind(this)(elem));

        accessor.functions.push(accessor['get' + elem.name]);
      }
    }

    this._createCallForAllElements(accessor);

    return accessor;
  }

  _isAlignable(size, place, elemSize) {
    return (size - 1 & place) === 0 && (size - 1 & elemSize) === 0; // tslint:disable-line:no-bitwise
  }

  _calculateAlignmentInfo(elemSize) {
    // tslint:disable-line
    let align = 0;

    for (const elem of this._elements) {
      elem.size = this._sizeInfo.get(elem.type);
      elem.offset = align;
      elem.align = this._isAlignable(elem.size, align, elemSize);

      if (elem.align && !this._swap) {
        this._neededTypedArrays.add(elem.type);
      } else {
        this._neededCacheArrays.add(elem.type);
      }

      align += elem.size;
    }
  }

  _calculateBlockSize() {
    let size = 0;

    for (const elem of this._elements) {
      size += this._sizeInfo.get(elem.type);
    }

    return size;
  }

  _createFunction(obj, name, func, ...args) {
    obj[name] = new Function(...args, func);
  }

  _createCache(obj) {
    if (!obj.cacheBuffer) {
      obj.cacheBuffer = new ArrayBuffer(8);
      obj.uint8Cache = new Uint8Array(obj.cacheBuffer);
    }

    this._neededCacheArrays.forEach(type => {
      if (type !== 'uint8' && type !== 'char8') {
        obj[type + 'Cache'] = new (this._arrayTypes.get(type))(obj.cacheBuffer);
      }
    });
  }
  /*
    fast swap
    x = (x & 0x0000FFFF) << 16 | (x & 0xFFFF0000) >> 16;
    x = (x & 0x00FF00FF) << 8 | (x & 0xFF00FF00) >> 8;
  */


  _createAccessor(blockSize) {
    const accessor = Object.create({});
    accessor.functions = [];
    let initFunc = 'this.numElements=numElements;this.offset=0;this.index=0;this.buffer=new Uint8Array(buffer);';

    this._neededTypedArrays.forEach(elem => {
      initFunc += 'this.' + elem + 'Array=new ' + this._arrayTypesAsString.get(elem) + '(buffer);';
    });

    this._createFunction(accessor, 'init', initFunc, 'buffer', 'numElements');

    this._createFunction(accessor, 'nextElement', 'this.offset+=' + blockSize + ';' + 'this.index+=1;');

    this._createFunction(accessor, 'reset', 'this.offset=0; this.index=0;');

    this._createFunction(accessor, 'getNumberOfElements', 'return this.numElements;');

    this._createCache(accessor);

    return accessor;
  }

  _createCallForAllElements(accessor) {
    let callFunc = 'this.reset();for(let _iIi=0;_iIi<this.numElements;_iIi++) {';
    let paramList = '';
    let comma = '';

    for (const elem of this._elements) {
      if (!!elem.name) {
        callFunc += 'let ' + elem.name + '=this.get' + elem.name + '();';
        paramList += comma + elem.name;
        comma = ',';
      }
    }

    callFunc += 'func(' + paramList + ');this.nextElement();}this.reset();';

    this._createFunction(accessor, 'callForAllElements', callFunc, 'func');
  }

  _createOffsetString(pos) {
    return pos !== 0 ? '+' + pos : '';
  }

  _createBytewiseCopy(offset, shuffle) {
    let res = '';

    for (const i in shuffle) {
      const index = shuffle[i];
      const pos = index + offset;
      res += 'this.uint8Cache[' + i + ']=this.buffer[this.offset' + this._createOffsetString(pos) + '];';
    }

    return res;
  } // -----------------------------------------------------------------------------------


  _createInt8Access(elem) {
    return 'return this.int8Array[this.offset+' + elem.offset + ']';
  } // -----------------------------------------------------------------------------------


  _createUint8Access(elem) {
    return 'return this.buffer[this.offset+' + elem.offset + ']';
  } // -----------------------------------------------------------------------------------


  _createInt16Access(elem) {
    let res = '';

    if (this._swap) {
      res = this._createBytewiseCopy(elem.offset, [1, 0]) + 'return this.int16Cache[0];';
    } else if (!elem.align) {
      res = this._createBytewiseCopy(elem.offset, [0, 1]) + 'return this.int16Cache[0];';
    } else {
      res = 'return this.int16Array[(this.offset' + this._createOffsetString(elem.offset) + ')>>1];';
    }

    return res;
  } // -----------------------------------------------------------------------------------


  _createUint16Access(elem) {
    let res = '';

    if (this._swap) {
      res = this._createBytewiseCopy(elem.offset, [1, 0]) + 'return this.uint16Cache[0];';
    } else if (!elem.align) {
      res = this._createBytewiseCopy(elem.offset, [0, 1]) + 'return this.uint16Cache[0];';
    } else {
      res = 'return this.uint16Array[(this.offset' + this._createOffsetString(elem.offset) + ')>>1];';
    }

    return res;
  } // -----------------------------------------------------------------------------------


  _createInt32Access(elem) {
    let res = '';

    if (this._swap) {
      res = this._createBytewiseCopy(elem.offset, [3, 2, 1, 0]) + 'return this.int32Cache[0];';
    } else if (!elem.align) {
      res = this._createBytewiseCopy(elem.offset, [0, 1, 2, 3]) + 'return this.int32Cache[0];';
    } else {
      res = 'return this.int32Array[(this.offset' + this._createOffsetString(elem.offset) + ')>>2];';
    }

    return res;
  } // -----------------------------------------------------------------------------------


  _createUint32Access(elem) {
    let res = '';

    if (this._swap) {
      res = this._createBytewiseCopy(elem.offset, [3, 2, 1, 0]) + 'return this.uint32Cache[0];';
    } else if (!elem.align) {
      res = this._createBytewiseCopy(elem.offset, [0, 1, 2, 3]) + 'return this.uint32Cache[0];';
    } else {
      res = 'return this.uint32Array[(this.offset' + this._createOffsetString(elem.offset) + ')>>2];';
    }

    return res;
  } // -----------------------------------------------------------------------------------


  _createFloat32Access(elem) {
    let res = '';

    if (this._swap) {
      res = this._createBytewiseCopy(elem.offset, [3, 2, 1, 0]) + 'return this.float32Cache[0];';
    } else if (!elem.align) {
      res = this._createBytewiseCopy(elem.offset, [0, 1, 2, 3]) + 'return this.float32Cache[0];';
    } else {
      res = 'return this.float32Array[(this.offset' + this._createOffsetString(elem.offset) + ')>>2];';
    }

    return res;
  } // -----------------------------------------------------------------------------------


  _createFloat64Access(elem) {
    let res = '';

    if (this._swap) {
      res = this._createBytewiseCopy(elem.offset, [7, 6, 5, 4, 3, 2, 1, 0]) + 'return this.float64Cache[0];';
    } else if (!elem.align) {
      res = this._createBytewiseCopy(elem.offset, [0, 1, 2, 3, 4, 5, 6, 7]) + 'return this.float64Cache[0];';
    } else {
      res = 'return this.float64Array[(this.offset+' + this._createOffsetString(elem.offset) + ')>>2];';
    }

    return res;
  }

}
//# sourceMappingURL=CrownAccessorBuilder.js.map